import ApiRequests from '../http/ApiRequests';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import get from 'lodash/get';
import axiosErrorHandler from '../http/AxiosErrorHandler';
import useConfirmDialog from './useConfirmDialog';
import { useHistory } from 'react-router';
import { routes } from '../routes/Routes';

const useTariffUpload = () => {
    const api = useMemo(() => new ApiRequests(), []);
    const { control } = useForm({});
    const [tariffGridData, setTariffGridData] = useState([]);
    const [spinner, setSpinner] = useState(false);
    const [generalErrorList, setGeneralErrorList] = useState([]);
    const [successMessage, setSuccessMessage] = useState(null);
    const confirm = useConfirmDialog();
    const history = useHistory();

    const tariffGridHeaders = [
        { name: 'Category', key: 'category' },
        { name: 'Product/Service', key: 'productService' },
        { name: 'Type', key: 'type' },
        { name: 'Description', key: 'description' },
        { name: 'Tariff For', key: 'tariffFor' },
        { name: 'Tariff Line (description)', key: 'tariffLineDescription' },
        { name: 'Currency', key: 'currencyCode' },
        { name: 'Tariff Answer', key: 'tariffAnswer' },
        { name: 'Tariff Point', key: 'tariffPoint' },
        { name: 'Last Updated', key: 'lastUpdated' },
        { name: 'Uploaded By', key: 'uploadBy' },
        { name: 'Reviewed By', key: 'reviewedBy' },
    ];

    const getLastApprovedTariffContent = useCallback(() => {
        api.getLastApprovedTariff()
            .then(res => setTariffGridData(processData(get(res, 'data', []))))
            .catch(err => {
                const [errorList] = axiosErrorHandler(err);
                setGeneralErrorList(errorList);
            });
    }, [api]);

    useEffect(() => {
        getLastApprovedTariffContent();
    }, [getLastApprovedTariffContent]);

    const processData = data => {
        return data.map(item => ({
            category: item.category,
            productService: item.productService,
            type: item.type,
            description: item.description,
            tariffFor: item.tariffFor,
            tariffLineDescription: item.tariffLineDescription,
            currencyCode: item.currencyCode,
            tariffAnswer: item.tariffAnswer,
            tariffPoint: item.tariffPoint,
            lastUpdated: item.lastUpdated,
            uploadBy: item.uploadBy,
            reviewedBy: item.reviewedBy,
        }));
    };

    const uploadHandler = ({ files }) => {
        if (files.length > 1) {
            setGeneralErrorList(['Only 1 file can be uploaded at a time']);
            return;
        }
        const [file] = files;
        const fileReader = new FileReader();
        fileReader.onload = () => {
            confirmUploadFileContent(file);
        };
        fileReader.readAsDataURL(file);
    };

    const uploadTariff = tariffFile => {
        setSpinner(true);
        let formData = new FormData();
        formData.append('excel-file', tariffFile, tariffFile.name);
        api.uploadTariff(formData)
            .then(res => {
                setSuccessMessage({ success: 'Successfully uploaded tariff' });
                const timer = setTimeout(() => {
                    history.push(routes.tariffApproval.path);
                }, 1000);
                setSpinner(false);
                return () => clearTimeout(timer);
            })
            .catch(err => {
                const [errorList] = axiosErrorHandler(err);
                setGeneralErrorList(errorList);
                setSpinner(false);
            });
    };

    const confirmUploadFileContent = tariffFile => {
        const confirmationData = {
            message: `Are you sure you want upload this file?`,
            header: 'Confirmation',
            icon: 'fa fa-question-circle-o',
            accept: () => uploadTariff(tariffFile),
        };
        confirm(confirmationData);
    };

    return {
        spinner,
        control,
        uploadHandler,
        tariffGridData,
        tariffGridHeaders,
        successMessage,
        generalErrorList,
    };
};

export default useTariffUpload;
